import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

type Num = number | string;

function num(n: Num, fallback = 0) {
  const v = typeof n === "string" ? parseFloat(n) : n;
  return Number.isFinite(v) ? (v as number) : fallback;
}

function fmt(n: number) {
  return new Intl.NumberFormat("no-NO").format(Math.round(n));
}

function fmtCurrency(n: number) {
  return new Intl.NumberFormat("no-NO", { style: "currency", currency: "NOK", maximumFractionDigits: 0 }).format(
    Math.round(n),
  );
}

export default function EnterpriseAdEconomy() {
  // Antagelser (redigerbare)
  const [dailyUsers, setDailyUsers] = React.useState<Num>(10000);
  const [viewsPerUser, setViewsPerUser] = React.useState<Num>(6); // visninger pr. bruker pr. dag
  const [adInsertRate, setAdInsertRate] = React.useState<Num>(0.6); // andel visninger der dagens native vises (0–1)
  const [adsPerDay, setAdsPerDay] = React.useState<Num>(1);        // behold 1
  const [pricePerDay, setPricePerDay] = React.useState<Num>(10000); // **10 000 NOK/dag**
  const [daysPerMonth, setDaysPerMonth] = React.useState<Num>(30);
  const [annualCosts, setAnnualCosts] = React.useState<Num>(3000000); // 3MNOK drift

  // Avledede tall
  const u = num(dailyUsers);
  const v = num(viewsPerUser);
  const r = Math.min(Math.max(num(adInsertRate), 0), 1);
  const a = Math.max(num(adsPerDay), 0);
  const p = Math.max(num(pricePerDay), 0);
  const dpm = Math.max(num(daysPerMonth), 1);
  const costs = Math.max(num(annualCosts), 0);

  const dailyImpressions = u * v * r;                // visninger av dagens native
  const totalDailyRevenue = p * a;                    // inntekt/dag
  const eCPM = dailyImpressions > 0 ? (totalDailyRevenue / dailyImpressions) * 1000 : 0;

  const monthlyRevenue = totalDailyRevenue * dpm;
  const annualRevenue = totalDailyRevenue * 365;
  const annualMargin = annualRevenue - costs;

  // Simulering for graf: eCPM vs. daglige brukere (4k–30k)
  const chartData = Array.from({ length: 14 }).map((_, i) => {
    const users = 4000 + i * 2000;
    const imp = users * v * r;
    const e = imp > 0 ? (totalDailyRevenue / imp) * 1000 : 0;
    return { users, eCPM: Math.max(e, 0) };
  });

  return (
    <section id="ad-economy" className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl md:text-2xl font-semibold">Enterprise: Annonseøkonomi</h2>
      </div>

      <Card className="p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Input-kolonne */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Input</h3>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Daglige brukere</Label>
                <Input inputMode="numeric" value={dailyUsers} onChange={(e) => setDailyUsers(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Visninger pr. bruker</Label>
                <Input inputMode="numeric" value={viewsPerUser} onChange={(e) => setViewsPerUser(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Visningsrate annonse (0–1)</Label>
                <Input inputMode="decimal" value={adInsertRate} onChange={(e) => setAdInsertRate(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Annonser pr. dag</Label>
                <Input inputMode="numeric" value={adsPerDay} onChange={(e) => setAdsPerDay(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Pris pr. dag</Label>
                <Input inputMode="numeric" value={pricePerDay} onChange={(e) => setPricePerDay(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Dager pr. mnd</Label>
                <Input inputMode="numeric" value={daysPerMonth} onChange={(e) => setDaysPerMonth(e.target.value)} />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label>Årlige driftskostnader</Label>
                <Input inputMode="numeric" value={annualCosts} onChange={(e) => setAnnualCosts(e.target.value)} />
              </div>
            </div>

            <div className="pt-2">
              <Button variant="outline" onClick={() => {
                setDailyUsers(10000);
                setViewsPerUser(6);
                setAdInsertRate(0.6);
                setAdsPerDay(1);
                setPricePerDay(10000);
                setDaysPerMonth(30);
                setAnnualCosts(3000000);
              }}>
                Tilbakestill antagelser
              </Button>
            </div>
          </div>

          {/* Nøkkeltall */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Nøkkeltall (daglig)</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="p-3 rounded-xl bg-gray-50">
                <div className="text-gray-500">Visninger (annonsen)</div>
                <div className="text-lg font-semibold">{fmt(dailyImpressions)}</div>
              </div>
              <div className="p-3 rounded-xl bg-gray-50">
                <div className="text-gray-500">Inntekt / dag</div>
                <div className="text-lg font-semibold">{fmtCurrency(totalDailyRevenue)}</div>
              </div>
              <div className="p-3 rounded-xl bg-gray-50">
                <div className="text-gray-500">eCPM</div>
                <div className="text-lg font-semibold">{fmtCurrency(eCPM)}</div>
              </div>
              <div className="p-3 rounded-xl bg-gray-50">
                <div className="text-gray-500">Visningsrate</div>
                <div className="text-lg font-semibold">{Math.round(r * 100)}%</div>
              </div>
            </div>

            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 pt-3">Måned/År</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="p-3 rounded-xl bg-gray-50">
                <div className="text-gray-500">Mnd. inntekt</div>
                <div className="text-lg font-semibold">{fmtCurrency(monthlyRevenue)}</div>
              </div>
              <div className="p-3 rounded-xl bg-gray-50">
                <div className="text-gray-500">Årsinntekt</div>
                <div className="text-lg font-semibold">{fmtCurrency(annualRevenue)}</div>
              </div>
              <div className="p-3 rounded-xl bg-gray-50 col-span-2">
                <div className="text-gray-500">Årlig margin (etter kostnader)</div>
                <div className={`text-lg font-semibold ${annualMargin >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                  {fmtCurrency(annualMargin)}
                </div>
              </div>
            </div>
          </div>

          {/* Graf + forklaring */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">eCPM vs. daglige brukere</h3>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="users" tickFormatter={(n) => fmt(n)} />
                  <YAxis tickFormatter={(n) => fmt(n)} />
                  <Tooltip
                    formatter={(val: any) => [fmtCurrency(val as number), "eCPM"]}
                    labelFormatter={(lab: any) => `Daglige brukere: ${fmt(lab)}`}
                  />
                  <Line type="monotone" dataKey="eCPM" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <p className="text-sm text-gray-600">
              Modellen er tilpasset feed-native annonser: ingen ad-block, høy synlighet og kontrollert visningsfrekvens.
              Én premium annonse pr. dag til {fmtCurrency(p)} er lønnsom ved moderate bruker-volumer, så lenge visningsraten
              i feeden holdes høy og innholdet er kvalitetsmessig på nivå med redaksjonelle innlegg.
            </p>
          </div>
        </div>
      </Card>

      <Card className="p-4 md:p-6">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">Scenario-tabell</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-gray-500">
              <tr>
                <th className="py-2 pr-4">Daglige brukere</th>
                <th className="py-2 pr-4">Visn./bruker</th>
                <th className="py-2 pr-4">Rate</th>
                <th className="py-2 pr-4">Visninger/dag</th>
                <th className="py-2 pr-4">Pris/dag</th>
                <th className="py-2 pr-4">eCPM</th>
                <th className="py-2 pr-4">Årsinntekt</th>
                <th className="py-2 pr-4">Års-margin</th>
              </tr>
            </thead>
            <tbody>
              {[6000, 8000, 10000, 15000, 20000].map((uu) => {
                const imp = uu * v * r;
                const e = imp > 0 ? (totalDailyRevenue / imp) * 1000 : 0;
                const yr = totalDailyRevenue * 365;
                const margin = yr - costs;
                return (
                  <tr key={uu} className="border-t">
                    <td className="py-2 pr-4">{fmt(uu)}</td>
                    <td className="py-2 pr-4">{v}</td>
                    <td className="py-2 pr-4">{Math.round(r * 100)}%</td>
                    <td className="py-2 pr-4">{fmt(imp)}</td>
                    <td className="py-2 pr-4">{fmtCurrency(totalDailyRevenue)}</td>
                    <td className="py-2 pr-4">{fmtCurrency(e)}</td>
                    <td className="py-2 pr-4">{fmtCurrency(yr)}</td>
                    <td className={`py-2 pr-4 ${margin >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{fmtCurrency(margin)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </section>
  );
}
